/**
 * @file ./views/layouts/danfe/producao/metricaTexto.js
 *
 * Estimativa de largura e altura de texto.
 *
 * O gerador original media o texto com `doc.heightOfString(texto, { width })`
 * do pdfkit, que consulta as métricas reais da fonte. O
 * `@react-pdf/renderer` não expõe medição antes de renderizar: a altura de
 * cada linha do quadro de produtos e a quebra de folha precisam ser
 * decididas **antes** de existir uma árvore para medir.
 *
 * A substituição é uma estimativa por largura média de caractere. Ela é
 * deliberadamente conservadora: superestimar produz uma linha extra
 * ocasional (perde-se alguns milímetros de folha), enquanto subestimar faz
 * o texto transbordar a moldura — o defeito que invalida o documento.
 */

/**
 * Largura média de caractere, em fração do corpo da fonte.
 *
 * O conteúdo dos quadros é impresso em caixa alta, e as maiúsculas do Times
 * New Roman são bem mais largas que a média do alfabeto completo (que
 * ficaria perto de 0,48). Daí o valor alto.
 */
export const LARGURA_MEDIA_CARACTERE = 0.62;

/**
 * Entrelinha natural do Times, em múltiplos do corpo — a soma de ascendente
 * e descendente que o pdfkit usa como altura de linha padrão.
 */
export const ENTRELINHA_NATURAL = 1.15;

/**
 * Negrito é mais largo que peso normal no mesmo corpo — medido diretamente
 * (largura real de glifo via `item.width` do pdf.js, sem influência de
 * quebra de linha) em três amostras de nome de bairro em Times-Bold 10pt
 * contra as mesmas em Times-Roman: fator médio 0,674 contra 0,637, uma
 * diferença de ~6%. 1,1 (10%) mantém a mesma margem de segurança que já
 * existe em `LARGURA_MEDIA_CARACTERE` para o peso normal.
 *
 * Faltou este fator quando `negrito: true` foi adicionado ao campo
 * Bairro/Distrito: a função de corte continuou usando a largura de peso
 * normal para medir texto que renderiza mais largo, deixando passar
 * caracteres demais e vazando da caixa.
 */
export const FATOR_NEGRITO = 1.1;

/**
 * Quantos caracteres cabem numa largura.
 *
 * @param {number} larguraPt
 * @param {number} corpoPt
 * @param {boolean} [negrito=false]
 * @returns {number} Sempre ao menos 1.
 */
export function caracteresPorLinha(larguraPt, corpoPt, negrito = false) {
  const fator = LARGURA_MEDIA_CARACTERE * (negrito ? FATOR_NEGRITO : 1);
  return Math.max(1, Math.floor(larguraPt / (corpoPt * fator)));
}

/**
 * Estima em quantas linhas um texto se quebra dentro de uma largura.
 * Quebras explícitas (`\n`) são respeitadas.
 *
 * @param {string} texto
 * @param {number} larguraPt
 * @param {number} corpoPt
 * @param {boolean} [negrito=false]
 * @returns {number} 0 se o texto for vazio.
 */
export function estimarLinhas(texto, larguraPt, corpoPt, negrito = false) {
  const conteudo = String(texto ?? '').trim();
  if (conteudo === '') return 0;

  const porLinha = caracteresPorLinha(larguraPt, corpoPt, negrito);

  return conteudo
    .split('\n')
    .reduce(
      (total, paragrafo) =>
        total + Math.max(1, Math.ceil(paragrafo.length / porLinha)),
      0,
    );
}

/**
 * Estima a altura ocupada por um texto — o equivalente ao
 * `doc.heightOfString` do pdfkit.
 *
 * @param {string} texto
 * @param {number} larguraPt
 * @param {number} corpoPt
 * @param {boolean} [negrito=false]
 * @returns {number} Altura em pontos.
 */
export function estimarAltura(texto, larguraPt, corpoPt, negrito = false) {
  return (
    estimarLinhas(texto, larguraPt, corpoPt, negrito) *
    corpoPt *
    ENTRELINHA_NATURAL
  );
}

/**
 * Corta o texto no que couber numa única linha, com reticências.
 *
 * Substitui o par `lineBreak: false` + `ellipsis: true` do pdfkit, que o
 * `@react-pdf/renderer` não implementa: um `<Text>` de largura fixa sempre
 * quebra. Cortar na origem é a única forma de garantir que o campo ocupe
 * exatamente uma linha.
 *
 * @param {string} texto
 * @param {number} larguraPt
 * @param {number} corpoPt
 * @param {boolean} [negrito=false]
 * @returns {string}
 */
export function truncarParaLargura(texto, larguraPt, corpoPt, negrito = false) {
  const conteudo = String(texto ?? '');
  const limite = caracteresPorLinha(larguraPt, corpoPt, negrito);

  if (conteudo.length <= limite) return conteudo;
  return `${conteudo.slice(0, Math.max(1, limite - 1))}…`;
}
